// ─── OpenAI-compatible HTTP ──────────────────────────────────────────────────
// Thin adapters over the three endpoints a non-omni voice stack is made of.
// Everything is (baseUrl, model, apiKey) — which is why LM Studio, whisper.cpp's
// server, Ollama, vLLM, a hosted API and a local TTS all drop into
// `local-pipeline` by config alone.
//
// It lives here because `local-pipeline` runs in BOTH placements — inside the
// Durable Object and inside the browser — and two copies of an HTTP client drift.
// Nothing below touches anything but `fetch`, `FormData`, `Blob` and `DataView`,
// so it is Worker-safe and browser-safe alike.

import type { AudioFormat } from './protocol.ts'

export interface Endpoint {
  baseUrl: string
  model: string
  apiKey?: string
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: string
  tool_call_id?: string
  tool_calls?: RawToolCall[]
}

export interface RawToolCall {
  id: string
  type: 'function'
  function: { name: string; arguments: string }
}

/** A tool declaration in OpenAI-neutral shape. */
export interface ToolSpec {
  name: string
  description: string
  parameters: Record<string, unknown>
}

/** Common to every call. `fetch` is injectable so a caller can be tested offline. */
interface Call {
  signal?: AbortSignal
  fetch?: typeof fetch
}

function url(ep: Endpoint, path: string): string {
  return `${ep.baseUrl.replace(/\/$/, '')}${path}`
}

function auth(ep: Endpoint, extra: Record<string, string> = {}): Record<string, string> {
  // Local servers ignore the key, but some require the header to exist at all.
  return { authorization: `Bearer ${ep.apiKey ?? 'local'}`, ...extra }
}

async function failed(res: Response, what: string): Promise<Error> {
  // A body that cannot be read must not mask the status it came with.
  return new Error(`${what} failed (${res.status}): ${(await res.text().catch(() => '')).slice(0, 300)}`)
}

// ── STT ──────────────────────────────────────────────────────────────────────
export async function transcribe(ep: Endpoint, audio: ArrayBuffer, opts: Call & { format: AudioFormat; locale?: string }): Promise<string> {
  const pcm = opts.format.encoding === 'pcm16'
  const form = new FormData()
  // Raw PCM isn't a recognisable audio file; a 44-byte WAV header makes it one.
  form.append('file', new Blob([pcm ? toWav(audio, opts.format) : audio], { type: pcm ? 'audio/wav' : 'application/octet-stream' }), 'turn.wav')
  form.append('model', ep.model)
  form.append('response_format', 'json')
  if (opts.locale) form.append('language', opts.locale.split('-')[0])

  const res = await (opts.fetch ?? fetch)(url(ep, '/audio/transcriptions'), {
    method: 'POST',
    headers: auth(ep),
    body: form,
    signal: opts.signal,
  })
  if (!res.ok) throw await failed(res, 'transcription')
  const body = (await res.json()) as { text?: string }
  return (body.text ?? '').trim()
}

// ── LLM ──────────────────────────────────────────────────────────────────────
export async function chat(
  ep: Endpoint,
  opts: Call & { messages: ChatMessage[]; tools?: ToolSpec[] },
): Promise<{ content: string; toolCalls: RawToolCall[] }> {
  const body: Record<string, unknown> = { model: ep.model, messages: opts.messages }
  if (opts.tools?.length) {
    body.tools = opts.tools.map((t) => ({ type: 'function', function: { name: t.name, description: t.description, parameters: t.parameters } }))
    body.tool_choice = 'auto'
  }

  const res = await (opts.fetch ?? fetch)(url(ep, '/chat/completions'), {
    method: 'POST',
    headers: auth(ep, { 'content-type': 'application/json' }),
    body: JSON.stringify(body),
    signal: opts.signal,
  })
  if (!res.ok) throw await failed(res, 'chat completion')

  const json = (await res.json()) as { choices?: Array<{ message?: { content?: string | null; tool_calls?: RawToolCall[] } }> }
  const message = json.choices?.[0]?.message
  return { content: (message?.content ?? '').trim(), toolCalls: message?.tool_calls ?? [] }
}

// ── TTS ──────────────────────────────────────────────────────────────────────
export async function speak(ep: Endpoint, text: string, opts: Call & { voice?: string; format: AudioFormat }): Promise<ArrayBuffer> {
  const res = await (opts.fetch ?? fetch)(url(ep, '/audio/speech'), {
    method: 'POST',
    headers: auth(ep, { 'content-type': 'application/json' }),
    body: JSON.stringify({
      model: ep.model,
      input: text,
      voice: opts.voice ?? 'alloy',
      // `pcm` keeps playback trivial: raw frames straight into the queue, no decoder.
      response_format: opts.format.encoding === 'pcm16' ? 'pcm' : 'wav',
    }),
    signal: opts.signal,
  })
  if (!res.ok) throw await failed(res, 'speech synthesis')
  return res.arrayBuffer()
}

/** Minimal 44-byte RIFF/WAVE header around raw little-endian PCM16. */
export function toWav(pcm: ArrayBuffer, format: AudioFormat): ArrayBuffer {
  const out = new ArrayBuffer(44 + pcm.byteLength)
  const view = new DataView(out)
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i))
  }
  ascii(0, 'RIFF')
  view.setUint32(4, 36 + pcm.byteLength, true)
  ascii(8, 'WAVEfmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, format.channels, true)
  view.setUint32(24, format.sampleRate, true)
  view.setUint32(28, format.sampleRate * format.channels * 2, true) // byte rate
  view.setUint16(32, format.channels * 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  ascii(36, 'data')
  view.setUint32(40, pcm.byteLength, true)
  new Uint8Array(out, 44).set(new Uint8Array(pcm))
  return out
}
